import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contentMgmtAPI } from '../services/api';
import PlayEditor from '../components/cms/PlayEditor';
import CatalogCard from '../../../shared/components/CatalogCard';
import '../components/cms/PlayEditor.css';

const CONTENT_TYPES = ['scenarios', 'questions', 'formations', 'teaching_modules', 'curriculum_tracks'];

export default function ContentManagerPage() {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [activeType, setActiveType] = useState('scenarios');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editJson, setEditJson] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [message, setMessage] = useState(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const { data } = await contentMgmtAPI.list(domain, activeType, params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]);
    }
    setLoading(false);
  }, [domain, activeType, statusFilter]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleCreate = async () => {
    try {
      const parsed = JSON.parse(editJson);
      await contentMgmtAPI.create(domain, activeType, parsed);
      setMessage({ type: 'success', text: 'Created successfully' });
      setEditItem(null);
      setEditJson('');
      loadItems();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Create failed' });
    }
  };

  const handleUpdate = async (itemId) => {
    try {
      const parsed = JSON.parse(editJson);
      await contentMgmtAPI.update(domain, activeType, itemId, parsed);
      setMessage({ type: 'success', text: 'Updated successfully' });
      setEditItem(null);
      setEditJson('');
      loadItems();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Update failed' });
    }
  };

  const handlePublish = async (itemId) => {
    try {
      await contentMgmtAPI.publish(domain, activeType, itemId);
      setMessage({ type: 'success', text: 'Published!' });
      loadItems();
    } catch (err) {
      setMessage({ type: 'error', text: 'Publish failed' });
    }
  };

  const handleArchive = async (itemId) => {
    try {
      await contentMgmtAPI.archive(domain, activeType, itemId);
      setMessage({ type: 'success', text: 'Archived' });
      loadItems();
    } catch (err) {
      setMessage({ type: 'error', text: 'Archive failed' });
    }
  };

  const handleSeed = async () => {
    try {
      const { data } = await contentMgmtAPI.seed(domain);
      setMessage({ type: 'success', text: `Seeded: ${JSON.stringify(data.seeded)}` });
      loadItems();
    } catch (err) {
      setMessage({ type: 'error', text: 'Seed failed' });
    }
  };

  const getItemId = (item) => {
    return item.scenario_id || item.template_id || item.formation_id || item.module_id || item.track_id || 'unknown';
  };

  const getItemTitle = (item) => {
    return item.title || item.display_name || item.prompt?.substring(0, 60) || getItemId(item);
  };

  return (
    <div className="content-manager-page">
      <div className="cms-header">
        <h1>Content Manager — {domain}</h1>
        <div className="cms-actions">
          <button className="btn-secondary" onClick={handleSeed}>Seed from YAML</button>
          <button className="btn-secondary" onClick={() => navigate(-1)}>Back</button>
        </div>
      </div>

      {message && (
        <div className={`cms-message ${message.type}`} onClick={() => setMessage(null)}>
          {message.text}
        </div>
      )}

      <div className="cms-tabs">
        {CONTENT_TYPES.map((ct) => (
          <button
            key={ct}
            className={`cms-tab ${activeType === ct ? 'active' : ''}`}
            onClick={() => { setActiveType(ct); setEditItem(null); }}
          >
            {ct.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="cms-toolbar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="review">Review</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <span className="cms-count">{total} items</span>
        <button className="btn-primary btn-small" onClick={() => { setEditItem('new'); setEditJson('{\n  \n}'); }}>
          + New
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="cms-item-list">
          {items.map((item) => {
            const id = getItemId(item);
            const itemIcon = item.type === 'tutorial' ? '📖' : activeType === 'scenarios' ? '🎭' : '📝';
            return (
              <CatalogCard
                key={id}
                title={getItemTitle(item)}
                description={item.summary || item.content_type || item.type || ''}
                icon={itemIcon}
                badges={[{ label: item.status || 'draft' }]}
                actions={[
                  { label: 'Edit', variant: 'ghost', onClick: () => { setEditItem(id); setEditJson(JSON.stringify(item, null, 2)); } },
                  ...(item.status !== 'published'
                    ? [{ label: 'Publish', variant: 'primary', onClick: () => handlePublish(id) }]
                    : []
                  ),
                  { label: 'Archive', variant: 'danger', onClick: () => handleArchive(id) },
                ]}
              />
            );
          })}
          {items.length === 0 && <p className="cms-empty">No items found. Click "+ New" or "Seed from YAML" to get started.</p>}
        </div>
      )}

      {editItem && (
        <div className="cms-editor-overlay">
          <div className="cms-editor">
            {activeType === 'scenarios' ? (
              <PlayEditor
                scenario={editJson}
                onSave={(updated) => {
                  setEditJson(JSON.stringify(updated, null, 2));
                  if (editItem === 'new') {
                    handleCreate();
                  } else {
                    handleUpdate(editItem);
                  }
                }}
                onCancel={() => { setEditItem(null); setEditJson(''); }}
              />
            ) : (
              <>
                <h3>{editItem === 'new' ? 'Create New' : `Edit ${editItem}`}</h3>
                <textarea
                  className="cms-json-editor"
                  value={editJson}
                  onChange={(e) => setEditJson(e.target.value)}
                  rows={20}
                />
                <div className="cms-editor-actions">
                  <button className="btn-primary" onClick={() => editItem === 'new' ? handleCreate() : handleUpdate(editItem)}>
                    {editItem === 'new' ? 'Create' : 'Save'}
                  </button>
                  <button className="btn-secondary" onClick={() => { setEditItem(null); setEditJson(''); }}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
